import { Box, Skeleton } from '@chakra-ui/react';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import DetailsView from '@/components/Templates/DetailsView';
import { formatDate } from '@/utils';
import type { ReturnOrderDetails } from '@/types/orderReturns';
import { shippingMethodItems } from '@/constants/orderConstants';

interface ReturnsOrderRequestModalContentProps {
	returnOrderData: any;
	isLoading: boolean;
}

/**
 * Get readable FedEx shipping method label
 */
const getFedExShippingMethodLabel = (code?: string | number): string => {
	if (!code) return '-';
	const codeStr = String(code);
	const method = shippingMethodItems.find(item => item.value === codeStr);
	return method?.label || codeStr;
};

/**
 * Get order request status label
 */
export const getOrderRequestStatus = (code?: number): string => {
	if (code === undefined || code === null) return '-';
	const statusMap: Record<number, string> = {
		953870000: 'Open',
		953870001: 'Closed',
		953870002: 'Cancelled',
		953870003: 'Pending',
		953870004: 'Sent to FB',
		953870005: 'Submitted to Manufacturing',
		953870006: 'Manufacturing',
		953870007: 'Shipped',
	};
	return statusMap[code] || String(code);
};

/**
 * Parse order request details into DetailsView format
 */
const parseOrderRequestDetails = (orderRequest: any) => {
	return [
		{
			title: 'Order Information',
			data: [
				{ label: 'Order Status Date', value: formatDate(orderRequest?.ta_orderrequestdate) },
				{ label: 'Clinic PO', value: orderRequest?.ta_clinicpo || '-' },
				{ label: 'Date of Service', value: formatDate(orderRequest?.ta_dos) },
				{ label: 'Status', value: getOrderRequestStatus(orderRequest?.ta_status) },
			],
		},
		{
			title: 'Shipping Details',
			data: [
				{ label: 'Street Address', value: orderRequest?.ta_streetaddress || '-' },
				{ label: 'City', value: orderRequest?.ta_cliniccity || '-' },
				{ label: 'State', value: orderRequest?.ta_clinicstate || '-' },
				{ label: 'Shipping Method', value: getFedExShippingMethodLabel(orderRequest?.ta_fedexshippingmethod) },
			],
		},
		{
			title: 'Additional Information',
			data: [
				{ label: 'Comments', value: orderRequest?.ta_comments || '-' },
			],
		},
	];
};

const ReturnsOrderRequestModalContent = ({ returnOrderData, isLoading }: ReturnsOrderRequestModalContentProps) => {
	const returnOrder = returnOrderData?.data as ReturnOrderDetails | undefined;
	const orderRequest = returnOrder?.orderRequest;
	
	const hasOrderRequest = !!orderRequest;
	const hasData = !isLoading && hasOrderRequest;
	const noData = !isLoading && !hasOrderRequest;

	return (
		<Box p={4}>
			<Show when={isLoading}>
				<Skeleton height="40px" mb={4} />
				<Skeleton height="200px" mb={4} />
				<Skeleton height="200px" />
			</Show>

			<Show when={noData}>
				<Box textAlign="center" py={8}>
					<FaceliftText fontSize="lg" fontWeight="bold">
						No Order Status Information
					</FaceliftText>
					<FaceliftText fontSize="sm">
						This return has no associated order request.
					</FaceliftText>
				</Box>
			</Show>

			<Show when={hasData}>
				<DetailsView
					data={parseOrderRequestDetails(orderRequest!)}
					gridConfig={{ base: '1fr', md: 'repeat(2, 1fr)' }}
					gap={{ base: 3, md: 6 }}
				/>
			</Show>
		</Box>
	);
};

export default ReturnsOrderRequestModalContent;

